import React from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton
} from '@mui/material'
import { getRequest, putRequest, postRequest } from 'Includes/api/apicall'
import axiosAPI from 'Includes/api/api'
import { GET_URL, PUT_URL, POST_URL } from 'Includes/urls'
import Swal from 'sweetalert2'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

export default function QuotationList(props) {
  const navigate = useNavigate()
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  // Follow-up modal state
  const [openModal, setOpenModal] = React.useState(false)
  const [selectedQuotation, setSelectedQuotation] = React.useState(null)
  const [followUpForm, setFollowUpForm] = React.useState({
    follow_up_time: '',
    next_follow_up_time: '',
    follow_up_status: ''
  })
  const [sendRow, setSendRow] = React.useState(null)
  const [sendForm, setSendForm] = React.useState({ phone: '', email: '' })
  const [sending, setSending] = React.useState(false)
  const [resultRow, setResultRow] = React.useState(null)
  const [resultText, setResultText] = React.useState('')

  const quoteStatus = (row) => row?.sales_meta?.quotation_status || (row?.sales_meta?.visit_id ? 'generated' : '')

  const loadData = () => {
    setLoading(true)
    getRequest(GET_URL.quotations.api, { limit: 100, pageno: 1 }, props)
      .then((res) => {
        if (res && res.status === 200) {
          setData(res.data?.data || [])
        }
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    loadData()
  }, [])

  const handlePrint = (row) => {
    navigate('/quotation/preview', { state: { data: row, autoPrint: true } })
  }

  const handleOpenFollowUp = (row) => {
    setSelectedQuotation(row)
    setFollowUpForm({
      follow_up_time: row.follow_up_time ? moment(row.follow_up_time).format('YYYY-MM-DDTHH:mm') : '',
      next_follow_up_time: row.next_follow_up_time ? moment(row.next_follow_up_time).format('YYYY-MM-DDTHH:mm') : '',
      follow_up_status: row.follow_up_status || 'following'
    })
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedQuotation(null)
  }

  const handleSaveFollowUp = async () => {
    if (!selectedQuotation) return
    try {
      await putRequest(`${PUT_URL.quotations.api}${selectedQuotation.id}/`, followUpForm, props)
      Swal.fire({ icon: 'success', title: 'Updated', text: 'Follow up updated successfully' })
      handleCloseModal()
      loadData()
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update follow up' })
    }
  }

  const openSend = (row) => {
    setSendRow(row)
    setSendForm({ phone: row.contact_phone || '', email: '' })
  }

  const downloadQuotationPdf = async (row) => {
    const storedLines = Array.isArray(row.line_items) && row.line_items.length
      ? row.line_items
      : Array.isArray(row.lineItems) ? row.lineItems : []
    const lineItems = storedLines.length
      ? storedLines
      : [{ item: row.service_name || row.plan_name || 'Quotation', amount: row.amount }]
    const salesMeta = row.sales_meta || row.salesMeta || {}
    const res = await axiosAPI.post(
      'agreements/quotation/pdf/',
      {
        company_name: row.company_name,
        company_address: row.company_address,
        gst: row.gst,
        notes: row.notes,
        lineItems,
        salesMeta,
      },
      { responseType: 'blob' }
    )
    const contentType = String(res.headers?.['content-type'] || '')
    if (!contentType.includes('pdf')) {
      throw new Error('Could not download the quotation PDF to attach.')
    }
    const blob = new Blob([res.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `quotation-${row.id || 'file'}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  const handleSend = async (via) => {
    if (!sendRow) return
    const popup = via === 'whatsapp' ? window.open('', '_blank') : null
    setSending(true)
    try {
      await downloadQuotationPdf(sendRow)
      const res = await postRequest(
        `${POST_URL.quotations.api}${sendRow.id}/send/`,
        { via, phone: sendForm.phone, email: sendForm.email },
        { ...props, return_error: true }
      )
      if (!res || res.status >= 400) {
        if (popup) popup.close()
        throw new Error(res?.data?.error || 'Failed to send quotation')
      }
      if (via === 'whatsapp' && res.data?.whatsapp_url) {
        if (popup) popup.location.href = res.data.whatsapp_url
        else window.open(res.data.whatsapp_url, '_blank', 'noopener')
      } else if (popup) {
        popup.close()
      }
      if (via === 'email' && res.data?.mailto) {
        window.location.href = res.data.mailto
      }
      Swal.fire({
        icon: 'success',
        title: via === 'whatsapp' ? 'WhatsApp opened' : 'Email opened',
        text: 'The quotation PDF was downloaded. Attach that file in the chat or email.',
      })
      setSendRow(null)
      loadData()
    } catch (e) {
      if (popup && !popup.closed) popup.close()
      Swal.fire({ icon: 'error', title: 'Could not send', text: e.message || 'Failed to send quotation' })
    } finally {
      setSending(false)
    }
  }

  const handleSaveResult = async () => {
    if (!resultRow) return
    setSending(true)
    try {
      const res = await postRequest(
        `${POST_URL.quotations.api}${resultRow.id}/results/`,
        { final_review: resultText },
        { ...props, return_error: true }
      )
      if (!res || res.status >= 400) {
        throw new Error(res?.data?.error || 'Failed to save result')
      }
      Swal.fire({ icon: 'success', title: 'Result saved' })
      setResultRow(null)
      setResultText('')
      loadData()
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Could not save', text: e.message || 'Failed to save result' })
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: '#0a0a0a' }}>
          Quotations
        </Typography>
        <Button variant="contained" onClick={() => navigate('/quotation/add')}>
          Add Quotation
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><b>ID</b></TableCell>
                <TableCell><b>Company</b></TableCell>
                <TableCell><b>Contact</b></TableCell>
                <TableCell><b>Plan / Service</b></TableCell>
                <TableCell><b>Amount</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Follow Up Status</b></TableCell>
                <TableCell align="right"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    No quotations found.
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.company_name}</TableCell>
                    <TableCell>
                      {row.contact_person || '—'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {row.contact_phone || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.plan_name || '—'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {row.service_name || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>₹{Number(row.amount).toFixed(2)}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {quoteStatus(row) === 'generated' && 'Quotation generated'}
                      {quoteStatus(row) === 'sent' && `Quotation sent${row.sales_meta?.sent_via ? ` · ${row.sales_meta.sent_via}` : ''}`}
                      {quoteStatus(row) === 'reviewed' && 'Final review'}
                      {!quoteStatus(row) && '—'}
                      {row.sales_meta?.final_review && (
                        <div>
                          <Typography variant="caption" color="text.secondary">
                            {row.sales_meta.final_review}
                          </Typography>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.follow_up_status ? (
                        <span style={{ textTransform: 'capitalize' }}>
                          {row.follow_up_status.replace('_', ' ')}
                        </span>
                      ) : '—'}
                      {row.next_follow_up_time && (
                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Next: {moment(row.next_follow_up_time).format('DD MMM YYYY HH:mm')}
                          </Typography>
                        </div>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => handlePrint(row)} sx={{ mr: 1, textTransform: 'none' }}>
                        Print
                      </Button>
                      <Button size="small" variant="contained" onClick={() => handleOpenFollowUp(row)} sx={{ textTransform: 'none', mr: 1 }}>
                        Add Follow Up
                      </Button>
                      {['generated', 'sent', 'reviewed'].includes(quoteStatus(row)) && (
                        <Button size="small" variant="contained" color="success" onClick={() => openSend(row)} sx={{ textTransform: 'none', mr: 1 }}>
                          Send
                        </Button>
                      )}
                      {(quoteStatus(row) === 'sent' || quoteStatus(row) === 'reviewed') && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setResultRow(row)
                            setResultText(row.sales_meta?.final_review || '')
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Update results
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Follow-up Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="xs">
        <DialogTitle>Add Follow Up</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            type="datetime-local"
            label="Follow up time"
            InputLabelProps={{ shrink: true }}
            margin="dense"
            value={followUpForm.follow_up_time}
            onChange={(e) => setFollowUpForm({ ...followUpForm, follow_up_time: e.target.value })}
          />
          <TextField
            fullWidth
            type="datetime-local"
            label="Next follow up time"
            InputLabelProps={{ shrink: true }}
            margin="dense"
            value={followUpForm.next_follow_up_time}
            onChange={(e) => setFollowUpForm({ ...followUpForm, next_follow_up_time: e.target.value })}
          />
          <TextField
            fullWidth
            select
            label="Status"
            margin="dense"
            value={followUpForm.follow_up_status}
            onChange={(e) => setFollowUpForm({ ...followUpForm, follow_up_status: e.target.value })}
          >
            <MenuItem value="following">Following</MenuItem>
            <MenuItem value="not_interested">Not Interested</MenuItem>
            <MenuItem value="converted">Converted</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFollowUp}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(sendRow)} onClose={() => setSendRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>Send quotation</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {sendRow?.company_name}. This opens WhatsApp or your email app. The quotation PDF downloads so you can attach it. You can send again after it has already been sent.
          </Typography>
          <TextField
            fullWidth
            label="WhatsApp number"
            margin="dense"
            value={sendForm.phone}
            onChange={(e) => setSendForm((prev) => ({ ...prev, phone: e.target.value }))}
          />
          <TextField
            fullWidth
            type="email"
            label="Email"
            margin="dense"
            value={sendForm.email}
            onChange={(e) => setSendForm((prev) => ({ ...prev, email: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendRow(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={sending} onClick={() => handleSend('whatsapp')}>
            WhatsApp
          </Button>
          <Button variant="contained" disabled={sending} onClick={() => handleSend('email')}>
            Email
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resultRow)} onClose={() => setResultRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>Update results</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="What happened"
            margin="dense"
            value={resultText}
            onChange={(e) => setResultText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultRow(null)}>Cancel</Button>
          <Button variant="contained" disabled={sending} onClick={handleSaveResult}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
